import {BdEquals} from '../equals/equals';

export type OverridePropertyKey =
  | 'admissionDate'
  | 'dismissalDate'
  | 'virtualClinicId'
  | 'fabKey'
  | 'market'
  | 'virtualBenchmarkingGroupId'
  | 'specialtyDepartmentId'
  | 'clinicsFilterId';

export type OverridePropertyDeleteState =
  | 'possible'
  | 'not-possible'
  | 'should-be-deleted';

export class OverrideProperty<T = any> {
  constructor(
    public tileId: string,
    public propertyKey: OverridePropertyKey,
    public value: T,
    public overrideValue: T,
    public deleteState: OverridePropertyDeleteState,
    public dependencyVirtualClinicId: string | undefined = undefined,
    public equalsFn: (a: T, b: T) => boolean = (a, b) => a === b,
    private _cloneFn: (cloneValue: T) => T = (cloneValue) =>
      JSON.parse(JSON.stringify(cloneValue))
  ) {
  }

  public clone(): OverrideProperty<T> {
    return new OverrideProperty<T>(
      this.tileId,
      this.propertyKey,
      this.value ? this._cloneFn(this.value) : this.value,
      this.overrideValue
        ? this._cloneFn(this.overrideValue)
        : this.overrideValue,
      this.deleteState,
      this.dependencyVirtualClinicId,
      this.equalsFn,
      this._cloneFn
    );
  }

  public equals(other: OverrideProperty<T>): boolean {
    return (
      BdEquals.equals(this.tileId, other.tileId) &&
      BdEquals.equals(this.propertyKey, other.propertyKey) &&
      BdEquals.equals(this.value, other.value, (a, b) => this.equalsFn(a, b))
    );
  }
}

export class OverridePropertyHelper {
  public static overrideProperty<T>(
    existingValue: T,
    propertyKey: OverridePropertyKey,
    override: OverrideProperty<T>[],
    cloneFn: (value: T) => T
  ): T | null {
    if (!override) {
      return cloneFn(existingValue) ?? null;
    }

    const overrideProperty = override.find(
      (it) =>
        BdEquals.equals(propertyKey, it.propertyKey) &&
        BdEquals.equals(existingValue, it.value, (a, b) => it.equalsFn(a, b))
    );

    if (overrideProperty) {
      if (overrideProperty.deleteState === 'should-be-deleted') {
        return null;
      } else {
        return cloneFn(overrideProperty.overrideValue);
      }
    } else {
      return cloneFn(existingValue) ?? null;
    }
  }

  public static overrideProperties<T>(
    existingValues: Array<T>,
    propertyKey: OverridePropertyKey,
    override: OverrideProperty<T>[],
    cloneFn: (value: T) => T
  ): Array<T> {
    return existingValues
      .map((it) => this.overrideProperty(it, propertyKey, override, cloneFn))
      .filter((it) => !!it);
  }
}
